import type { ReactNode } from "react";
import { ArtikelImage } from "@/components/ArtikelImage";
import {
  TimesheetEditorAngebotBadge,
  TimesheetEditorHeroBadge,
  TimesheetEditorRowActions,
  TimesheetEditorTableCell,
  TimesheetEditorTableRow,
} from "@/features/timesheetFlow/components/TimesheetEditorTable";
import {
  angebotMaterialBadgeLabel,
  isAngebotMaterialRecord,
  isHeroMaterialRecord,
} from "@/features/timesheetFlow/lib/timesheetMaterialSource";
import type { TimesheetMaterialRecord } from "@/features/timesheetFlow/timesheetFlowTypes";

function formatQty(menge: number): string {
  if (!Number.isFinite(menge)) return "—";
  return String(menge).replace(".", ",");
}

function CellText({ children, muted }: { children: ReactNode; muted?: boolean }) {
  return (
    <span className={muted ? "text-label-secondary" : "text-label"}>{children}</span>
  );
}

export function TimesheetMaterialRow({
  material,
  onEdit,
  onRemove,
}: {
  material: TimesheetMaterialRecord;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const isHero = isHeroMaterialRecord(material);
  const isAngebot = isAngebotMaterialRecord(material);
  const angebotBadge = isAngebot ? angebotMaterialBadgeLabel(material) : null;

  return (
    <TimesheetEditorTableRow>
      <TimesheetEditorTableCell className="w-[10%] font-mono text-sm tabular-nums">
        {formatQty(material.menge)}
      </TimesheetEditorTableCell>
      <TimesheetEditorTableCell className="w-[10%] text-sm">
        <CellText>{material.einheit || "—"}</CellText>
      </TimesheetEditorTableCell>
      <TimesheetEditorTableCell className="hidden font-mono text-xs md:table-cell">
        <CellText muted={!material.ean}>{material.ean || "—"}</CellText>
      </TimesheetEditorTableCell>
      <TimesheetEditorTableCell className="hidden font-mono text-xs lg:table-cell">
        <CellText muted={!material.hero_nr}>{material.hero_nr || "—"}</CellText>
      </TimesheetEditorTableCell>
      <TimesheetEditorTableCell className="min-w-[10rem] text-sm leading-snug">
        {material.text ? (
          <span className="inline-flex items-start gap-2 font-medium">
            <ArtikelImage src={material.image_src} alt="" size="sm" expandable />
            <span>
            {material.text}
            {isHero && !isAngebot ? (
              <>
                {" "}
                <TimesheetEditorHeroBadge />
              </>
            ) : null}
            {angebotBadge ? (
              <>
                {" "}
                <TimesheetEditorAngebotBadge label={angebotBadge} />
              </>
            ) : null}
            </span>
          </span>
        ) : (
          "—"
        )}
      </TimesheetEditorTableCell>
      <TimesheetEditorTableCell className="w-[4.75rem]">
        <TimesheetEditorRowActions
          onEdit={onEdit}
          onRemove={onRemove}
          editLabel={
            isAngebot
              ? "Nur Menge bearbeiten"
              : isHero
                ? "Menge und Einheit bearbeiten"
                : "Material bearbeiten"
          }
        />
      </TimesheetEditorTableCell>
    </TimesheetEditorTableRow>
  );
}
